import React, {useCallback, useMemo, useState} from 'react';
import {
	HoverCard,
	HoverCardType,
	Icon,
	Label,
	Separator,
	Stack,
	TooltipDelay,
	TooltipHost,
	useTheme,
} from '@fluentui/react';
import {useTranslation} from 'react-i18next';
import {
	InfinityListColumn,
	InfinityListColumnFilterOptions,
	InfinityListColumnMetaData,
	InfinityListFilter,
} from 'components/InfinityList';
import {CellWithEntityLink} from 'components/EntityList/CellWithEntityLink';
import {
	getHoverClassNames,
	renderArrayField,
	renderDateString,
	renderPhase,
	renderStatus,
	renderVkosPersona,
} from 'components/EntityList/ColumnRenderers';
import {getStatusTag} from 'components/StatusTag';
import {LoadSpinner} from 'components/LoadWrapper/LoadSpinner';
import {DEFAULT_DATE} from 'components/EntityList/EntityUtils';

import {formatDateTime} from 'i18n/localeDateFormat';
import {Keyword, RegDocFilterOptions, RegulatoryDocument} from 'types';
import {getVoProSummary} from './ReportsPage';

import {GetVoProRegDocQueryQuery} from './hooks/RegulatoryDocuments.generated';
import {
	getOptionsSorted,
	queryStringMongoArraySTD,
	queryStringMongoDateSTD,
	queryStringMongoEnumSTD,
} from 'components/InfinityList/InfinitListStandardHooks';
import {
	GetAllKeywordsQuery,
	useGetAllKeywordsQuery,
} from 'features/AdminSection/hooks/keywords.generated';
import {GetAllMarketsQuery} from 'features/AdminSection/hooks/markets.generated';
import {useGetAllVkosQuery} from 'features/AdminSection/hooks/vko.generated';

export type IReportsPageColumns = {
	(
		hoveredRegulatoryDocument: RegulatoryDocument | undefined,
		setHoveredRegulatoryDocument: (
			hoveredRegulatoryDocument: RegulatoryDocument | undefined,
		) => void,
		fetchSummaryData: (arrIdsNext: string[]) => Promise<RegulatoryDocument[]>,
		loadingSummary: boolean,
	): InfinityListColumn[];
};

export type IReportsPageFilterOptions = {
	(
		regDocFilterOptions: RegDocFilterOptions | undefined,
		keywordData?: GetAllKeywordsQuery | undefined,
		marketData?: GetAllMarketsQuery | undefined,
	): InfinityListColumnFilterOptions[];
};

export const useReportsPageColumn: IReportsPageColumns = (
	hoveredRegulatoryDocument,
	setHoveredRegulatoryDocument,
	fetchSummaryData,
	loadingSummary,
) => {
	const {t: tWorkFlow} = useTranslation('common/enums', {
		keyPrefix: 'WorkflowStatus',
	});

	const {t: tList} = useTranslation('features/reports', {
		keyPrefix: 'ReportsList',
	});

	const theme = useTheme();
	const classnames = getHoverClassNames(theme);

	const {i18n} = useTranslation();

	const {data: keywordData} = useGetAllKeywordsQuery();
	const {data: dataVkos} = useGetAllVkosQuery();

	const handleSummaryRender = useCallback(
		(item: RegulatoryDocument) => (
			<HoverCard
				key={item.id}
				type={HoverCardType.plain}
				onCardVisible={async () => {
					const arrRegulatoryDocumentNext = await fetchSummaryData([item.id]);
					setHoveredRegulatoryDocument(arrRegulatoryDocumentNext.at(0)!);
				}}
				plainCardProps={{
					onRenderPlainCard() {
						const isLoading = loadingSummary || !hoveredRegulatoryDocument;

						const {historyHtmlString, reportSummaryString} = getVoProSummary(
							hoveredRegulatoryDocument,
							tList,
						);

						return (
							<Stack className={classnames.hover} tokens={{childrenGap: 5}}>
								<Label>
									{item.regulation?.regulationNumber ?? ''} | {item.name}
								</Label>
								{!(isLoading || historyHtmlString === '<p></p>') && (
									<div className={classnames.detailCluster}>
										{tList('ChangeHistory')}
										<Separator />
										<div
											dangerouslySetInnerHTML={{__html: historyHtmlString}}
										/>
									</div>
								)}
								<div className={classnames.detailCluster}>
									{tList('Summary')}
									<Separator />
									{isLoading && (
										<LoadSpinner
											extraStyles={{root: {height: '60px', top: '30px'}}}
										/>
									)}
									{!isLoading && (
										<div
											dangerouslySetInnerHTML={{__html: reportSummaryString}}
										/>
									)}
								</div>
							</Stack>
						);
					},
				}}
			>
				<Icon
					iconName='InfoSolid'
					style={{
						display: 'flex',
						alignItems: 'center',
						fontSize: 'small',
						justifyContent: 'center',
					}}
				/>
			</HoverCard>
		),
		[loadingSummary, hoveredRegulatoryDocument],
	);

	const {t} = useTranslation('features/pogisdocuments', {
		keyPrefix: 'PogisDocumentsList',
	});

	const widthSTD120 = {minWidth: 120, maxWidth: 120};
	const widthSTD150 = {minWidth: 150, maxWidth: 200};
	const filterArraySimple = {
		filterOnFilter: true,
		filterType: 'Array',
	};
	const filterArray = {
		...filterArraySimple,
		onRender: renderArrayField(),
	};
	const resizeMulti = {isResizable: true, isMultiline: true};
	const filterArray150 = {...widthSTD150, ...resizeMulti, ...filterArray};
	const sortableDate = {sortable: true, filterType: 'Date'};
	const sortableDate120 = {
		...widthSTD120,
		...sortableDate,
		onRender: renderDateString(),
	};
	const phase = {
		...sortableDate120,
		filterType: 'Date',
		onRender: renderPhase(),
	};

	const keywords = useMemo(
		() => (keywordData?.keywords ?? []) as Keyword[],
		[keywordData],
	);

	const columns: InfinityListColumn[] = useMemo(
		() => [
			{
				key: 'tooltip',
				name: tList('Summary'),
				fieldName: 'tooltip',
				minWidth: 120,
				maxWidth: 120,
				onRender: handleSummaryRender,
			},
			{
				key: 'regulationNumber',
				name: tList('Regulation'),
				fieldName: 'regulationNumber',
				minWidth: 200,
				maxWidth: 300,
				...resizeMulti,
				...filterArraySimple,
				hiddenAlways: true,
			},
			{
				key: 'version',
				name: tList('Version'),
				fieldName: 'version',
				minWidth: 200,
				maxWidth: 300,
				...resizeMulti,
				onRender: item => (
					<CellWithEntityLink href={`/regulatoryDocuments/${item.id}`}>
						<strong>{item.regulation?.regulationNumber ?? ''}</strong> |{' '}
						{item.name}
					</CellWithEntityLink>
				),
			},
			{
				key: 'modifiedAt',
				name: tList('ModifiedAt'),
				fieldName: 'modifiedAt',
				...widthSTD120,
				...sortableDate,
				onRender(item) {
					const fieldValue = item.workflow.clearingTimestamp;
					const date =
						fieldValue && fieldValue !== DEFAULT_DATE
							? formatDateTime(new Date(fieldValue), i18n)
							: '';
					return <div>{`${date}`}</div>;
				},
			},
			{
				key: 'contactPersons',
				name: tList('ContactPerson'),
				fieldName: 'contactPersons',
				...filterArray150,
				minWidth: 180,
				onRender: renderVkosPersona(keywords, dataVkos),
			},
			{
				key: 'markets',
				name: tList('Markets'),
				fieldName: 'markets',
				...filterArray150,
			},
			{
				key: 'mainKeywords',
				name: tList('MainKeywords'),
				fieldName: 'mainKeywords',
				...filterArray150,
			},
			{
				key: 'keywords',
				name: tList('Keywords'),
				fieldName: 'keywords',
				...filterArray150,
			},
			{
				key: 'status',
				name: tList('Status'),
				fieldName: 'status',
				...widthSTD120,
				sortable: true,
				filterType: 'Array',
				onRender: renderStatus('RegulatoryDocumentStatus'),
			},
			{
				key: 'dateEffective',
				name: tList('DateEffective'),
				fieldName: 'dateEffective',
				...sortableDate120,
			},
			{
				key: 'dateNewTypes',
				name: tList('DateNewTypes'),
				fieldName: 'dateNewTypes',
				...sortableDate120,
			},
			{
				key: 'dateNewRegistration',
				name: tList('DateNewRegistration'),
				fieldName: 'dateNewRegistration',
				...sortableDate120,
			},
			{
				key: 'comprehensive',
				name: tList('DateImplementation'),
				fieldName: 'comprehensive',
				...sortableDate120,
			},
			{
				key: 'modelYear',
				name: tList('ModelYear'),
				fieldName: 'modelYear',
				...filterArray150,
				onRender: item => (item.modelYear ? t('MY') + item.modelYear : ''),
			},
			{
				key: 'phaseIn',
				name: tList('PhaseIn'),
				fieldName: 'phaseIn',
				...phase,
			},
			{
				key: 'phaseOut',
				name: tList('PhaseOut'),
				fieldName: 'phaseOut',
				...phase,
			},
			{
				key: 'workflowStatus',
				name: tList('WorkflowStatus'),
				fieldName: 'workflowStatus',
				...widthSTD120,
				filterType: 'Array',
				onRender: item => getStatusTag(item.workflow.status, theme, tWorkFlow),
			},
		],
		[handleSummaryRender, keywords, dataVkos],
	);

	return columns;
};

const queryStringMongoDateModelYear = (): ((
	filter: InfinityListFilter,
) => string) => {
	const handleConversion = (filter: InfinityListFilter) => {
		return `{${filter.columnKey}: {$${filter.operation}:  ${filter.selection} }}`;
	};

	return handleConversion;
};

const queryStringMongoDatePhase = (): ((
	filter: InfinityListFilter,
) => string) => {
	const handleConversion = (filter: InfinityListFilter) => {
		return `{ ${filter.columnKey}: { $elemMatch:{ date: {$${
			filter.operation
		}: ISODate('${filter.selection.slice(0, 10)}')}}}}`;
	};

	return handleConversion;
};

export const useReportsPageColumnMetaData = () => {
	const columnMetaData: InfinityListColumnMetaData[] = [
		{
			key: 'regulationNumber',
			mongoDBFn: queryStringMongoArraySTD('regulationRef._id'),
		},
		{
			key: 'contactPersons',
			mongoDBFn: queryStringMongoArraySTD('contactPersonRefs._id'),
		},
		{
			key: 'markets',
			mongoDBFn: queryStringMongoArraySTD('marketRefs._id'),
		},
		{
			key: 'mainKeywords',
			mongoDBFn: queryStringMongoArraySTD('mainKeywordRefs._id'),
		},
		{
			key: 'keywords',
			mongoDBFn: queryStringMongoArraySTD('keywordRefs._id'),
		},
		{
			key: 'status',
			mongoDBFn: queryStringMongoEnumSTD('status'),
		},
		{
			key: 'workflowStatus',
			mongoDBFn: queryStringMongoEnumSTD('workflow.status'),
		},
		{
			key: 'modifiedAt',
			mongoDBFn: queryStringMongoDateSTD(),
			sortInverted: true,
		},
		{
			key: 'dateEffective',
			mongoDBFn: queryStringMongoDateSTD(),
			sortInverted: true,
		},
		{
			key: 'dateNewTypes',
			mongoDBFn: queryStringMongoDateSTD(),
			sortInverted: true,
		},
		{
			key: 'dateNewRegistration',
			mongoDBFn: queryStringMongoDateSTD(),
			sortInverted: true,
		},
		{
			key: 'comprehensive',
			mongoDBFn: queryStringMongoDateSTD(),
			sortInverted: true,
		},
		{
			key: 'modelYear',
			mongoDBFn: queryStringMongoDateModelYear(),
		},
		{
			key: 'phaseIn',
			mongoDBFn: queryStringMongoDatePhase(),
		},
		{
			key: 'phaseOut',
			mongoDBFn: queryStringMongoDatePhase(),
		},
	];
	return columnMetaData;
};
export const useReportsPageFilterOptions: IReportsPageFilterOptions = (
	regDocFilterOptions,
	keywordData,
	marketData,
) => {
	const {t: tStatus} = useTranslation('common/enums', {
		keyPrefix: 'RegulatoryDocumentStatus',
	});
	const {t: tWorkFlow} = useTranslation('common/enums', {
		keyPrefix: 'WorkflowStatus',
	});
	const {t} = useTranslation('features/pogisdocuments', {
		keyPrefix: 'PogisDocumentsList',
	});

	const columnFilterOptions: InfinityListColumnFilterOptions[] = useMemo(() => {
		const queryData = regDocFilterOptions;
		const marketsAll = marketData?.markets ?? [];
		const keywordsAll = keywordData?.keywords ?? [];

		const marketOptions = queryData?.marketOptions ?? [];
		const mainKeywordOptions = queryData?.mainKeywordOptions ?? [];
		const keywordOptions = queryData?.keywordOptions ?? [];

		const marketOptionsSorted = getOptionsSorted(marketsAll, marketOptions);

		const mainKeywordOptionsSorted = getOptionsSorted(
			keywordsAll,
			mainKeywordOptions,
		);

		const keywordOptionsSorted = getOptionsSorted(keywordsAll, keywordOptions);

		const filterOptions = [
			{
				key: 'regulationNumber',
				fieldValuesUnique: (queryData?.regulationOptions ?? [])
					.slice()
					.sort((a, b) => a.name.localeCompare(b.name)),
			},
			{
				key: 'contactPersons',
				fieldValuesUnique: queryData?.contactPersonOptions,
			},
			{
				key: 'markets',
				fieldValuesUnique: marketOptionsSorted,
			},
			{
				key: 'mainKeywords',
				fieldValuesUnique: mainKeywordOptionsSorted,
			},
			{
				key: 'keywords',
				fieldValuesUnique: keywordOptionsSorted,
			},
			{
				key: 'status',
				fieldValuesUnique: (queryData?.statusOptions ?? []).map(x => {
					return {optionKey: x.optionKey, name: tStatus(x.name)};
				}),
			},
			{
				key: 'workflowStatus',
				fieldValuesUnique: (queryData?.workFlowOptions ?? []).map(x => {
					return {optionKey: x.optionKey, name: tWorkFlow(x.name)};
				}),
			},
			{
				key: 'modelYear',
				fieldValuesUnique: (queryData?.modelYearOptions ?? [])
					.filter(x => x.name !== '-1')
					.map(x => {
						return {optionKey: x.optionKey, name: t('MY') + x.name};
					}),
			},
		];

		return filterOptions.map(x => {
			return {...x, fieldValuesUnique: x.fieldValuesUnique ?? []};
		});
	}, [regDocFilterOptions, keywordData, marketData]);
	return columnFilterOptions;
};
